import { useEffect, useMemo, useState } from "react";
import { Button } from "react-aria-components";
import { useNavigate } from "react-router";

import notificationReadIcon from "@/assets/figma/popup-toolbar/read-all.svg";
import notificationChatIcon from "@/assets/figma/popup-toolbar/new-chat.svg";
import readAllIcon from "@/assets/figma/popup-toolbar/read-all.svg";
import { MerryButton } from "@/shared/ui/MerryButton";
import { MerryPopupSearchToolbar } from "@/shared/ui/MerryPopupSearchToolbar";

import { useAppSelector } from "@/app/hooks";
import { selectAuthActor } from "@/features/auth/authSessionSlice";
import {
  firstConversationShopCandidate,
  resolveConversationShopBindingId,
} from "@/features/chat/conversationShopScope";
import { useGetConversationHistoryInfiniteQuery } from "@/features/chat-history/conversationHistoryApi";
import { mergeConversationHistoryPages } from "@/features/chat-history/conversationHistoryModel";
import {
  useGetNotificationListInfiniteQuery,
  useGetNotificationSummaryQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from "@/features/notifications/notificationApi";
import {
  getNotificationActionPath,
  resolveNotificationConversationShop,
  type NotificationCategory,
  type UserNotification,
} from "@/features/notifications/notificationSchemas";
import { selectWorkspaceStoreSeedBindingId } from "@/features/settings/activeStoreSlice";
import { useGetMerchantStoreBindingsQuery } from "@/features/settings/storeManagementApi";

const categoryLabels: Record<NotificationCategory, string> = {
  approval_execution: "审批与执行",
  billing_resource: "账务与资源",
  conversation_task: "对话与任务",
  report_file: "报告与文件",
  store_connection: "店铺与连接",
  team_invitation: "团队邀请",
};

const workflowStatusLabels = {
  running: "任务处理中",
  waiting_approval: "等待审批",
  succeeded: "任务已完成",
  partial_failed: "任务部分完成",
  failed: "任务未完成",
  cancelled: "任务已取消",
} as const;

export function NotificationsView() {
  const actor = useAppSelector(selectAuthActor);
  const navigate = useNavigate();
  const organizationId = actor?.activeOrganizationId ?? "";
  const userId = actor?.userId ?? "";
  const selectedBindingId = useAppSelector((state) =>
    selectWorkspaceStoreSeedBindingId(state, organizationId),
  );
  const storeBindings = useGetMerchantStoreBindingsQuery(organizationId, {
    skip: !organizationId,
  });
  const shopBindingId = storeBindings.currentData
    ? resolveConversationShopBindingId(
        selectedBindingId,
        storeBindings.currentData.bindings,
      )
    : null;
  const conversationsQuery = useGetConversationHistoryInfiniteQuery(
    {
      organizationId,
      query: "",
      shopBindingId: shopBindingId ?? "",
      userId,
      view: "active",
    },
    { skip: !organizationId || !userId || shopBindingId === null },
  );
  const shopByConversationId = useMemo(() => {
    const shops = new Map<string, string>();
    if (shopBindingId === null) return shops;
    for (const item of mergeConversationHistoryPages(
      conversationsQuery.currentData?.pages,
    )) {
      const itemShopBindingId = firstConversationShopCandidate(item.shopBindingId);
      if (itemShopBindingId) shops.set(item.id, itemShopBindingId);
    }
    return shops;
  }, [conversationsQuery.currentData?.pages, shopBindingId]);
  const [expandedNotificationId, setExpandedNotificationId] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState("");
  const [query, setQuery] = useState("");
  const [operationError, setOperationError] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchDraft.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  const listQuery = useGetNotificationListInfiniteQuery(
    { category: "all", organizationId, query, view: "all" },
    { skip: !organizationId },
  );
  const summaryQuery = useGetNotificationSummaryQuery(organizationId, {
    pollingInterval: 60_000,
    skip: !organizationId,
  });
  const [markRead, markReadState] = useMarkNotificationReadMutation();
  const [markAllRead, markAllReadState] = useMarkAllNotificationsReadMutation();
  const items = useMemo(() => {
    const unique = new Map<string, UserNotification>();
    for (const page of listQuery.data?.pages ?? []) {
      for (const item of page.items) unique.set(item.id, item);
    }
    return [...unique.values()];
  }, [listQuery.data]);
  const revision = summaryQuery.data?.revision ?? 0;
  const unreadCount = summaryQuery.data?.unreadCount ?? 0;
  const isBusy = markReadState.isLoading || markAllReadState.isLoading;

  const readNotification = async (notification: UserNotification) => {
    if (notification.readAt || revision < 1) return true;
    try {
      await markRead({
        expectedRevision: revision,
        notificationId: notification.id,
        organizationId,
      }).unwrap();
      setOperationError("");
      return true;
    } catch {
      setOperationError("通知状态已变化，已为你刷新，请重试。");
      void summaryQuery.refetch();
      return false;
    }
  };

  const openNotification = async (notification: UserNotification) => {
    if (!(await readNotification(notification)) || !notification.action) return;
    const action = notification.action;
    if (action.kind === "open_conversation" || action.kind === "open_approval") {
      const actionShopBindingId = resolveNotificationConversationShop(
        action,
        shopByConversationId.get(action.resourceId),
        shopBindingId,
      );
      if (!actionShopBindingId) {
        if (!storeBindings.currentData) {
          setOperationError(
            storeBindings.isError
              ? "无法确认这段任务所属的店铺，店铺列表暂时无法读取。"
              : "店铺信息还在加载，请稍后再打开详情。",
          );
          return;
        }
        if (storeBindings.currentData.bindings.length > 1) {
          setOperationError("无法确认这段任务所属的店铺，请先选择店铺后再打开详情。");
          return;
        }
      }
      setOperationError("");
      navigate(getNotificationActionPath(action, actionShopBindingId));
      return;
    }
    setOperationError("");
    navigate(getNotificationActionPath(action));
  };

  const readAll = async () => {
    if (revision < 1 || unreadCount === 0) return;
    try {
      await markAllRead({ expectedRevision: revision, organizationId }).unwrap();
      setOperationError("");
    } catch {
      setOperationError("通知状态已变化，已为你刷新，请重试。");
      void summaryQuery.refetch();
    }
  };

  const renderCompactRow = (notification: UserNotification) => (
    <NotificationRow key={notification.id} notification={notification} disabled={isBusy}
      expanded={expandedNotificationId === notification.id}
      onToggle={() => setExpandedNotificationId(id => id === notification.id ? null : notification.id)}
      onOpen={() => void openNotification(notification)}
      onRead={() => void readNotification(notification)} />
  );

  const initialLoading = listQuery.isLoading || summaryQuery.isLoading;
  const failed = listQuery.isError || summaryQuery.isError;

  return (
    <section className="notification-center notification-center--popover" data-testid="notification-center-popover">
      <div className="notification-center__toolbar">
        <MerryPopupSearchToolbar value={searchDraft} onSearchChange={setSearchDraft} placeholder="搜索通知内容" actionIcon={readAllIcon} actionLabel="全部已读" isActionDisabled={isBusy || unreadCount === 0} onAction={() => void readAll()} />
      </div>

      {operationError ? <p className="notification-center__error" role="alert">{operationError}</p> : null}

      {initialLoading ? (
        <NotificationState message="正在加载通知…" />
      ) : failed && items.length === 0 ? (
        <NotificationState
          actionLabel="重新加载"
          message="通知暂时无法加载"
          onAction={() => {
            void listQuery.refetch();
            void summaryQuery.refetch();
          }}
        />
      ) : items.length === 0 ? (
        <NotificationState message="暂时没有符合条件的通知" />
      ) : (
        <div className="notification-center__list-frame">
        <div className="notification-center__list">
          <p className="notification-center__group-label">未读通知</p>
          {items.filter(item => !item.readAt && item.category !== "team_invitation").map(notification => renderCompactRow(notification))}
          <p className="notification-center__group-label">邀约通知</p>
          {items.filter(item => !item.readAt && item.category === "team_invitation").map(notification => renderCompactRow(notification))}
          <p className="notification-center__group-label">已读通知</p>
          {items.filter(item => item.readAt).map(notification => renderCompactRow(notification))}
        </div>
        </div>
      )}

      {listQuery.hasNextPage ? (
        <Button
          className="notification-center__load-more"
          isDisabled={listQuery.isFetchingNextPage}
          onPress={() => void listQuery.fetchNextPage()}
        >
          {listQuery.isFetchingNextPage ? "正在加载…" : "加载更多"}
        </Button>
      ) : null}
    </section>
  );
}

function NotificationRow({
  disabled,
  expanded = false,
  onToggle,
  notification,
  onOpen,
  onRead,
}: {
  disabled: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  notification: UserNotification;
  onOpen: () => void;
  onRead: () => void;
}) {
  return (
    <article
      className="notification-center-row notification-center-row--compact"
      data-notification-read={notification.readAt ? "true" : "false"}
      data-expanded={expanded ? "true" : "false"}
      onClick={event => { if (!(event.target as HTMLElement).closest("button")) onToggle?.(); }}
    >
      <span aria-hidden="true" className="notification-center-row__status" />
      <div className="notification-center-row__copy">
        <div className="notification-center-row__meta">
          <span>{categoryLabels[notification.category]}</span>
          {notification.workflow ? <span>{workflowStatusLabels[notification.workflow.status]}</span> : null}
          <time dateTime={notification.occurredAt}>{formatTime(notification.occurredAt)}</time>
        </div>
        <h2><Button className="notification-center-row__title-button" aria-expanded={expanded} onPress={onToggle}>{notification.title}</Button></h2>
        <p className={expanded ? "notification-center-row__summary-expanded" : undefined}>{notification.summary}</p>
      </div>
      {expanded && <div className="notification-center-row__actions">
        {!notification.readAt ? (
          <MerryButton size={34} hierarchy="level1" icon={<img alt="" src={notificationReadIcon} />} className="notification-center-row__secondary" isDisabled={disabled} onPress={onRead}>
            已读
          </MerryButton>
        ) : null}
        {notification.action ? (
          <MerryButton size={34} hierarchy="level1" icon={<img alt="" src={notificationChatIcon} />} className="notification-center-row__open" isDisabled={disabled} onPress={onOpen}>
            {(notification.action.kind === "open_conversation" || notification.action.kind === "open_approval") ? "聊天" : "查看详情"}
          </MerryButton>
        ) : null}
      </div>}
    </article>
  );
}

function NotificationState({
  actionLabel,
  message,
  onAction,
}: {
  actionLabel?: string;
  message: string;
  onAction?: () => void;
}) {
  return (
    <div className="notification-center__state" role="status">
      <p>{message}</p>
      {actionLabel && onAction ? (
        <Button className="notification-center__load-more" onPress={onAction}>{actionLabel}</Button>
      ) : null}
    </div>
  );
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
  }).format(date);
}
